import React, { Suspense, useEffect, useCallback } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { useStore } from './stores/store'
import { observer } from 'mobx-react-lite';
import * as KeyNames from 'src/utils/KeyNames'
import { ToastContainer } from 'react-toastify';
import AlertDialog from 'src/components/dialogs/AlertDialog'
import './scss/style.scss'
import 'semantic-ui-css/semantic.min.css'
import 'react-toastify/dist/ReactToastify.min.css';
import 'simplebar-react/dist/simplebar.min.css'
import './styles.css'
import ApiAgent from "src/api/ApiAgent";
import moment from 'moment'
import { runInAction } from 'mobx';

const loading = (
  <div className="pt-3 text-center">
     <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
)
// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))

// Pages
const Login = React.lazy(() => import('./views/login/Login'))
const ConfirmAccount = React.lazy(() => import('./views/login/ConfirmAccount'))
const ForgotPassword = React.lazy(() => import('./views/login/ForgotPassword'))
const ChangePassword = React.lazy(() => import('./views/login/ChangePassword'))
const Register = React.lazy(() => import('./views/login/Register'))
const Page404 = React.lazy(() => import('./views/page404/Page404'))
const Page500 = React.lazy(() => import('./views/page500/Page500'))

const App = () => {

  const {userStore, commonStore} = useStore();


  useEffect(() => {

    userStore.initAuth();

     // First run happens after 60 minutes
    const interval = setInterval(onHandleClearStorage, 60 * 60 * 1000);

    window.addEventListener('focus', onHandleFocusChange);
    window.addEventListener('visibilitychange', onHandleFocusChange);

    return () => {
      clearInterval(interval); // cleanup when component unmounts
      window.removeEventListener('visibilitychange', onHandleFocusChange);
      window.removeEventListener('focus', onHandleFocusChange);
    }
    // eslint-disable-next-line   
  }, []);

  const onHandleClearStorage = () => {

    console.log("Clearing sessionStorage...");
    window.sessionStorage.clear();
  };

  const onHandleFocusChange = useCallback(() => {

    const pathName = window.location.pathname;
    if (userStore.isAuthenticated && pathName !== "/register" && pathName !== "/login") {

      requestApplicationVersionAsync();
    }
    // eslint-disable-next-line   
  }, []);

  async function requestApplicationVersionAsync() {

    const lastReqStr = window.sessionStorage.getItem(KeyNames.LastVersionTime);
    if (lastReqStr) {
      
      const lastReqTime = moment(lastReqStr, "DD/MM/YYYY HH:mm:ss");
      const compareTime = moment().add(-5, 'minutes');

      if (lastReqTime > compareTime) {
        // console.log("request within 5 minutes....");
        return;
      }
    }

    // set version request time to session cache...
    window.sessionStorage.setItem(KeyNames.LastVersionTime, moment().format('DD/MM/YYYY HH:mm:ss'));

    try {

      const response = await ApiAgent.WebUser.getAppVersion();
      runInAction(() => {

        if (response) {

          const tempVersion = response.trim();
          const thisVersion = window.localStorage.getItem(KeyNames.ApplicationVersion);

          if (thisVersion) {

            if (thisVersion !== tempVersion) {
              console.log("App, New Version:", tempVersion + ", This Version: " + thisVersion);

              // Save this version to User Store. After clicking reload button, this version will be saved to Local Store...
              userStore.setWebVersion( tempVersion );

              // -------------------------------------------------------------
              // Save current web version to local storage and reload page 
              // -------------------------------------------------------------
              window.localStorage.setItem(KeyNames.ApplicationVersion, userStore.webVersion);
  
              // Delete caches...
              if ('caches' in window) {
                caches.keys().then((names) => {
                    names.forEach(async (name) => {
                        await caches.delete(name)
                    })
                });
              }
  
              window.location.reload( true );

              // commonStore.setAlertMsg({isOpen: true, isSuccess: false, isReload: true, 
              //    title: "New Version Released", 
              //    message: "<span style='font-size: 16px'>To refresh application, please click the below 'RELOAD' button</span><br><br><span style='font-size: 13px'>If you still have this issue, please logout and login again</span>"});
            }
          }
          else {
            console.log("App, Version:", tempVersion);
            window.localStorage.setItem(KeyNames.ApplicationVersion, tempVersion);
          }
        }
      })
    }
    catch(error) {
      console.log(error);
    }
    finally{
    }
  }

  
  const getUserConfirmation = (dialogKey, callback) => {
    // use "message" as Symbol-based key
    const dialogTrigger = window[Symbol.for(dialogKey)];

    if (dialogTrigger) {
      // pass the callback to delegate
      // to the invoked dialog
      return dialogTrigger(callback);
    }

    // Fallback to allowing navigation
    callback(true);
  };

  return (
    <>
      <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={false} closeOnClick 
            pauseOnFocusLoss draggable pauseOnHover theme="light" />
      <AlertDialog/>    

      <BrowserRouter getUserConfirmation={getUserConfirmation}>
        <Suspense fallback={loading} >
          <Routes>
            <Route exact path="/register" name="Sign Up Page" element={<Register />} />
            <Route exact path="/login" name="Login Page" element={<Login />} />
            <Route exact path="/confirm" name="Confirm Account Page" element={<ConfirmAccount />} />
            <Route exact path="/resetPassword" name="Reset Password Page" element={<ChangePassword />} />
            <Route exact path="/forgot" name="Forgot Password Page" element={<ForgotPassword />} /> 
            <Route exact path="/404" name="Page 404" element={<Page404 />} />
            <Route exact path="/500" name="Page 500" element={<Page500 />} />
            <Route path="*" name="Home" element={userStore.isAuthenticated ? <DefaultLayout /> : <Login />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  )
}

export default observer(App)

